import { SANDBOX_PRESET_TAG as T } from '../presetTags'
import { SANDBOX_PRESET_BODY_CSS, type SandboxComponentPreset } from '../../sandboxPresetTypes'

/**
 * Two modes. **Determinate**: the slider sets fill width and `aria-valuenow`. **Indeterminate**: a CSS
 * animation shows stripes, the slider is disabled, and the ARIA value is removed so screen readers
 * don’t hear a fake percentage.
 */
const shell = SANDBOX_PRESET_BODY_CSS

const preset: SandboxComponentPreset = {
  id: 'progress-bar-strip',
  category: 'loading',
  title: 'Progress bar (strip)',
  description:
    'Determinate fill from a range slider; toggle indeterminate striped sliding chunk.',
  tags: [T.progress, T.animation, T.css],
  html: `<h2>Strip progress</h2>
<p class="hint">Move the slider or switch modes.</p>

<!-- Native range control: "input" fires continuously while dragging -->
<label class="lbl"><span>Percent</span><input type="range" id="pct" min="0" max="100" value="42" /></label>

<!-- role=progressbar + aria-* updated from JS in determinate mode -->
<div class="track" id="track" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="42"><div class="fill" id="fill"></div></div>

<p><button type="button" id="mode">Use indeterminate</button></p>`,
  css: `${shell}

h2 {
  color: var(--nogrok-preview-primary);
  margin: 0 0 0.5rem;
  font-size: 1.1rem;
}

.hint {
  margin: 0 0 1rem;
  font-size: 0.85rem;
  opacity: 0.85;
}

.lbl {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  margin-bottom: 0.75rem;
  max-width: 18rem;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--nogrok-preview-on-surface-muted);
}

/* Track = unfilled rail; .fill grows from the left. Inline-size container so indeterminate translate can use cqi (= track width). */
.track {
  height: 10px;
  border-radius: 999px;
  background: var(--nogrok-preview-surface-rail);
  overflow: hidden;
  border: 1px solid var(--nogrok-preview-outline);
  max-width: 22rem;
  container-type: inline-size;
}

.fill {
  height: 100%;
  width: 42%;
  background: linear-gradient(90deg, var(--nogrok-preview-primary), var(--nogrok-preview-accent-warm));
  border-radius: 999px;
  transition: width 0.2s ease;
}

/* Indeterminate: chunk is 35% of track; slide from fully off-left until fully off-right (100cqi = track width here). */
.track.indet .fill {
  width: 35% !important;
  background: repeating-linear-gradient(-45deg, var(--nogrok-preview-primary), var(--nogrok-preview-primary) 8px, var(--nogrok-preview-accent-deep) 8px, var(--nogrok-preview-accent-deep) 16px);
  animation: indet 1s linear infinite;
}

/* 100cqi = track width; 100% = this element’s width — so the chunk’s right edge reaches the track’s right edge */
@keyframes indet {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(calc(100cqi - 100%));
  }
}

button {
  font: inherit;
  padding: 0.45rem 0.9rem;
  border-radius: 6px;
  border: 1px solid var(--nogrok-preview-primary);
  background: color-mix(in srgb, var(--nogrok-preview-primary) 12.5%, transparent);
  color: var(--nogrok-preview-on-surface);
  cursor: pointer;
}`,
  js: `const pct = document.getElementById('pct')
const fill = document.getElementById('fill')
const track = document.getElementById('track')
const mode = document.getElementById('mode')
let indet = false

function sync() {
  if (!pct || !fill || !track || indet) return
  const v = Number(pct instanceof HTMLInputElement ? pct.value : 0)
  fill.style.width = v + '%'
  track.setAttribute('aria-valuenow', String(v))
}

pct?.addEventListener('input', sync)
mode?.addEventListener('click', () => {
  indet = !indet
  track?.classList.toggle('indet', indet)
  if (mode) mode.textContent = indet ? 'Use determinate' : 'Use indeterminate'
  if (pct instanceof HTMLInputElement) pct.disabled = indet
  // When unknown progress: don’t expose a numeric valuenow
  if (indet) track?.removeAttribute('aria-valuenow')
  else sync()
})
sync()`,
}

export default preset
